import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Popover, type PopoverProps } from '../popover';
import { dropdownMenuVariants, dropdownMenuItemVariants } from './variants';

/** A single selectable option in a {@link DropdownMenu}. */
export interface DropdownMenuItem<T extends string> {
  /** Stable value used for selection + matching against `value`. */
  value: T;
  /** Label rendered next to the icon. */
  label: string;
  /** Optional leading icon URL (rendered as a 14px `<img>`). */
  iconSrc?: string;
}

/**
 * Props for {@link DropdownMenu}.
 *
 * @typeParam T - The union of option values.
 */
export interface DropdownMenuProps<T extends string> {
  /** Whether the menu is visible. */
  open: boolean;
  /** Called on Escape or an outside click. */
  onClose: () => void;
  /** The always-rendered trigger the menu anchors to. */
  anchor: ReactNode;
  /** The selectable options. */
  items: DropdownMenuItem<T>[];
  /** The currently selected value — its row is highlighted. */
  value: T;
  /** Called with the chosen value; the menu closes immediately after. */
  onSelect: (value: T) => void;
  /** Edge to align the menu to its anchor — forwarded to {@link Popover}. */
  align?: PopoverProps['align'];
  /** Extra classes for the menu surface. */
  className?: string;
}

/**
 * Small anchored context menu for picking one option from a short list — built
 * on {@link Popover} (which owns positioning, outside-click, and Escape). Each
 * row shows an optional icon, a label, and a chevron; the active row is
 * highlighted. Selecting a row calls `onSelect` then closes the menu.
 *
 * @example
 * ```tsx
 * <DropdownMenu
 *   open={open}
 *   onClose={close}
 *   anchor={<button>Buy In Dollars</button>}
 *   items={[{ value: 'dollars', label: 'Buy In Dollars', iconSrc: dollarIcon }]}
 *   value={format}
 *   onSelect={setFormat}
 * />
 * ```
 */
export function DropdownMenu<T extends string>({
  open,
  onClose,
  anchor,
  items,
  value,
  onSelect,
  align,
  className,
}: DropdownMenuProps<T>) {
  function handleSelect(next: T) {
    onSelect(next);
    onClose();
  }

  return (
    <Popover open={open} onClose={onClose} anchor={anchor} align={align} className={cn(dropdownMenuVariants(), className)}>
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          onClick={() => handleSelect(item.value)}
          className={dropdownMenuItemVariants({ selected: item.value === value })}
        >
          {item.iconSrc && <img src={item.iconSrc} alt="" className="size-3.5 shrink-0" />}
          <span className="whitespace-nowrap">{item.label}</span>
        </button>
      ))}
    </Popover>
  );
}
